import React, { useState, useEffect } from 'react'
import './dash.css'
import './ResponsiveDash.css'
import dashOne from '../Images/dashone.svg'
import dashTwo from '../Images/ayurveda.jpg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserCheck} from '@fortawesome/free-solid-svg-icons'
import { faIdCard } from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from 'react-router-dom';
// firebase
import {db} from '../firebase';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, query, getDocs, where } from "firebase/firestore";
import { DEFAULT_USD_TO_KES_RATE, fetchUsdToKesRate, formatMoneyFromKes, resolveStudentCurrency } from '../utils/currency';
import { getInterviewPortalPath } from '../utils/interviewScheduling';
import { useLocation } from '../LocationContext';
import { fetchPublicWeather } from '../utils/publicDocsApi';

const DASHBOARD_CACHE_KEY = 'ubunifu-dashboard-summary-cache-v1';
const DEFAULT_ADMISSION_FEE_AMOUNT_KES = 2000;
const ADMISSION_FEE_SETTINGS_DOC = 'admissionFee';

const readDashboardCache = (uid) => {
    if (typeof window === "undefined" || !uid) return null;
    try {
        const rawCache = window.sessionStorage.getItem(DASHBOARD_CACHE_KEY);
        if (!rawCache) return null;
        const parsedCache = JSON.parse(rawCache);
        return parsedCache?.[uid] || null;
    } catch (error) {
        return null;
    }
};

const writeDashboardCache = (uid, value) => {
    if (typeof window === "undefined" || !uid) return;
    try {
        const rawCache = window.sessionStorage.getItem(DASHBOARD_CACHE_KEY);
        const parsedCache = rawCache ? JSON.parse(rawCache) : {};
        parsedCache[uid] = value;
        window.sessionStorage.setItem(DASHBOARD_CACHE_KEY, JSON.stringify(parsedCache));
    } catch (error) {
        // Ignore cache write issues and continue with live state.
    }
};

const getDateObject = (value) => {
    if (!value) return null;
    if (typeof value?.toDate === "function") return value.toDate();
    if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return null;
    return parsedDate;
};

const formatInterviewDateTime = (value, fallback = "Not scheduled") => {
    const dateObj = getDateObject(value);
    if (!dateObj) return fallback;

    return dateObj.toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone: "Africa/Nairobi",
        timeZoneName: "short"
    });
};

const getInterviewStatusLabel = (status) => {
    if (status === "active") return "Live now";
    if (status === "scheduled") return "Scheduled";
    if (status === "ended") return "Ended";
    return "Ready";
};



function DashComp() {

    //states
    const [loggedInUser, setLoggedInUser] = useState("");
    const [feeBalance, setFeeDeposit] = useState("");
    const [balanceThisMonth, setBalanceThisMonth] = useState("");
    const [courseFeeInstallments, setCourseFeeInstallments] = useState("");
    const [amountPaidThisMonth, setAmountPaidThisMonth] = useState("");
    const [admissionFeeStatus, setAdmissionFeeStatus] = useState('');
    const [interviewRecord, setInterviewRecord] = useState(null);
    const [studentCurrencyCode, setStudentCurrencyCode] = useState('KES');
    const [usdToKesRate, setUsdToKesRate] = useState(DEFAULT_USD_TO_KES_RATE);
    const [admissionFeeAmountKes, setAdmissionFeeAmountKes] = useState(DEFAULT_ADMISSION_FEE_AMOUNT_KES);

    const [theUserStatus, setuserStatus] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [now, setNow] = useState(() => new Date());
    const [weatherImg, setWeatherImg] = useState();
    const [weatherText, setWeatherText] = useState();
    const [thecurrentWeatherTemp, setcurrentWeatherTemp] = useState();
    const navigate = useNavigate();
    const { location, currency } = useLocation();
    const normalizedUserStatus = (theUserStatus || "").toString().trim().toLowerCase();
    const canViewStudentId = normalizedUserStatus === "student" || normalizedUserStatus === "completed";
    const admissionFeePending = !isLoading && !canViewStudentId && admissionFeeStatus !== 'paid';
    const interviewMeetingId = interviewRecord?.meetingId || interviewRecord?.roomName || "";
    const showInterviewDashboardCard = !isLoading && !!interviewMeetingId && interviewRecord?.status !== "ended";
    const admissionFeeAmount = formatMoneyFromKes(admissionFeeAmountKes, studentCurrencyCode, usdToKesRate);





    const auth = getAuth();

    useEffect(() => {
        let isMounted = true;

        const fetchFinanceSettings = async () => {
            try {
                const [rate, admissionFeeSnap] = await Promise.all([
                    fetchUsdToKesRate(db),
                    getDoc(doc(db, 'Settings', ADMISSION_FEE_SETTINGS_DOC))
                ]);
                const admissionFeeAmount = Number(admissionFeeSnap.data()?.amountKes || DEFAULT_ADMISSION_FEE_AMOUNT_KES);

                if (isMounted) {
                    setUsdToKesRate(rate);
                    setAdmissionFeeAmountKes(
                        Number.isFinite(admissionFeeAmount) && admissionFeeAmount > 0
                            ? admissionFeeAmount
                            : DEFAULT_ADMISSION_FEE_AMOUNT_KES
                    );
                }
            } catch (error) {
                console.error('Failed to load finance settings:', error);
            }
        };

        fetchFinanceSettings();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (!user) {
                setInterviewRecord(null);
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            try {
                const uid = user.uid;
                const cachedSummary = readDashboardCache(uid);
                if (cachedSummary) {
                    setLoggedInUser(cachedSummary.loggedInUser || "");
                    setuserStatus(cachedSummary.userStatus || "");
                }

                const q = query(collection(db, "users"), where("userId", "==", uid));
                const [userSnapshot, interviewSnapshot] = await Promise.all([
                    getDocs(q),
                    getDoc(doc(db, "interviewCalls", uid))
                ]);
                let course = "";
                let dashboardSummary = null;

                setInterviewRecord(interviewSnapshot.exists() ? { id: interviewSnapshot.id, ...interviewSnapshot.data() } : null);

                userSnapshot.forEach((doc)=>{
                    const username = doc.data().bio;
                    const userData = doc.data();
                    const userStatus = userData.userStatus;
                    course = userData.course;
                    const secondaryCourses = userData.secondaryCourses || [];

                    setAdmissionFeeStatus((userData.admissionFeeStatus || '').toString().toLowerCase());
                    setStudentCurrencyCode(resolveStudentCurrency(userData));

                    setuserStatus(userStatus);
                    setLoggedInUser(username);

                    dashboardSummary = {
                        loggedInUser: username || "",
                        userStatus: userStatus || "",
                        studentIdLabel: doc.data().studentId || doc.data().userId || uid,
                        courseName: course || "",
                        updatedAt: Date.now(),
                    };
                });

                if (dashboardSummary) {
                    writeDashboardCache(uid, dashboardSummary);
                }

                const { feeInstallments } = await viewCoursePrice(course);

                const courseQ = query(collection(db, "Income"), where("studentId", "==", uid));  
                const incomeSnapshot = await getDocs(courseQ);
                let thisMonthsIncome = 0;

                incomeSnapshot.forEach((doc)=>{
                    const incomeAmount = doc.data().incomeAmount;
                    const paymentDate = doc.data().paymentDate;
                    const theIncome = parseInt(incomeAmount);

                    const todaysDate = new Date();
                    const thisYear = todaysDate.getFullYear();
                    const thisMonth = todaysDate.getMonth();
                    const thisMonthYear = thisMonth + " " + thisYear;

                    const datePaid = paymentDate.toDate().toUTCString();
                    const date = new Date(datePaid);
                    const monthPaid = date.getMonth();
                    const yearPaid = date.getFullYear();              
                    const paidThisMonthYear = monthPaid + " " + yearPaid;

                    if(thisMonthYear === paidThisMonthYear){
                        thisMonthsIncome = theIncome + thisMonthsIncome;
                    }
                });

                setAmountPaidThisMonth(thisMonthsIncome);
                const mainBalance = Math.max(0, (feeInstallments || 0) - thisMonthsIncome);
                setBalanceThisMonth(mainBalance);
            } catch (error) {
                console.error("Error loading dashboard data:", error);
            } finally {
                setIsLoading(false);
            }
        });

        return () => unsubscribe();
    }, [auth]);

   async function  viewCoursePrice(course){
        if(!course) return { feeDeposit: 0, feeInstallments: 0 };
        const courseQ = query(collection(db, "courses"), where("mcourseName", "==", course));
        const QuerySnapshot = await getDocs(courseQ);
        let feeDeposit = 0;
        let feeInstallments = 0;
        QuerySnapshot.forEach((doc)=>{
            feeDeposit = doc.data().feeDeposit;
            feeInstallments = doc.data().feeInstallments;
            setCourseFeeInstallments(feeInstallments);
            setFeeDeposit(feeDeposit);
        });
        return { feeDeposit, feeInstallments };


    }

    //date functions
    const todaysDate = new Date();
    const todayDay = todaysDate.getDate();
    const todayDayOfWeek = todaysDate.getDay();
    const todayMonth = todaysDate.getMonth();
    const todayYear = todaysDate.getFullYear();

    const weekArray = ["Sunday","Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const monthArray = ["January","February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    const [todayDayState] = useState(todayDay);
    const [todayWeekState] = useState(weekArray[todayDayOfWeek]);
    const [todayMonthState] = useState(monthArray[todayMonth]);
    const [todayYearState] = useState(todayYear);
    //dates end

    const localTimezone = location?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'Africa/Nairobi';
    const nairobiTime = now.toLocaleTimeString('en-KE', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'Africa/Nairobi'
    });
    const nairobiDate = now.toLocaleDateString('en-KE', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'Africa/Nairobi'
    });
    const formattedTime = now.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: localTimezone
    });
    const localDateLabel = now.toLocaleDateString([], {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: localTimezone
    });
    const localDayNumber = now.toLocaleDateString([], {
        day: 'numeric',
        timeZone: localTimezone
    });
    const localTimezoneLabel = localTimezone.replace('_', ' ');
    const localPlaceLabel = [
        location?.city,
        location?.region,
        currency?.countryName || location?.country
    ].filter(Boolean).join(', ');

    useEffect(() => {
        const clock = window.setInterval(() => {
            setNow(new Date());
        }, 60000);

        return () => window.clearInterval(clock);
    }, []);

    useEffect(() => {
        const city = location?.city || 'Nairobi';

        fetchPublicWeather({ city, aqi: 'no' })
            .then((payload) => {
                const weather = payload?.weather?.current || {};
                setWeatherImg(weather?.condition?.icon || '');
                setWeatherText(weather?.condition?.text || '');
                setcurrentWeatherTemp(weather?.feelslike_c);
            })
            .catch((err) => {
                console.error(err);
                setWeatherImg('');
                setWeatherText('Weather unavailable');
                setcurrentWeatherTemp(null);
            });
    }, [location?.city]);
    


  return (
    <div className='dashComp'>

        <div className='dashFlex1'>
            <div className='dash1'>
                <div className='dash1Cont'>
                    <h4>Welcome</h4>
                    {isLoading ? (
                        <span className='skeleton-line dashSkeletonLine dashSkeletonLine--wide' />
                    ) : (
                        <p>{loggedInUser}</p>
                    )}
                </div>
                <img src={dashOne} alt="" />
            </div>

            <div className='dash2'>

                <div className='cliRight3'>
                    <div className='cliRight3Block'>
                        <span className='cliRight3Label'>Ubunifu HQ</span>
                        <p>{nairobiDate}</p>
                        <h2>{nairobiTime}</h2>
                        <p>Nairobi, Kenya</p>
                    </div>

                    {weatherImg ? <img src={weatherImg} alt={weatherText || 'Weather'} className='cloidIcoClient' /> : null}

                    <div className='cliRight3Block cliRight3WeatherBlock'>
                        <h2>{thecurrentWeatherTemp ? `${thecurrentWeatherTemp}°` : '--'}</h2>
                        <p>{weatherText || 'Weather loading'}</p>
                        <small>Live from Nairobi campus</small>
                    </div>
                </div>

                <div className={`dChild3 dashMiniCard ${isLoading ? "dashCardLoading" : ""}`}>  
                    <div className='dashMiniIcon'>
                        <FontAwesomeIcon icon={faUserCheck} />
                    </div>
                    <div className='dashMiniMeta'>
                        <p>Status</p>
                        {isLoading ? (
                            <span className='skeleton-line dashSkeletonLine' />
                        ) : (
                            <h5>{theUserStatus || "Student"}</h5>
                        )}
                        <span>Account standing</span>
                    </div>
                </div>

                <div
                    className={`dChild4 studentIdCard ${isLoading ? "dashCardLoading" : ""}`}
                    role={isLoading || !canViewStudentId ? undefined : "button"}
                    tabIndex={isLoading || !canViewStudentId ? -1 : 0}
                    onClick={isLoading || !canViewStudentId ? undefined : () => navigate("/student-id")}
                    onKeyDown={(event) => !isLoading && canViewStudentId && event.key === "Enter" && navigate("/student-id")}
                >
                    <div className='studentIdCardInfo'>
                        <div className='studentIdIcon'>
                            <FontAwesomeIcon icon={faIdCard} />
                        </div>
                        <div>
                            <h6>School ID</h6>
                            {isLoading ? (
                                <span className='skeleton-line dashSkeletonLine dashSkeletonLine--wide' />
                            ) : canViewStudentId ? (
                                <span>Tap to view your school ID</span>
                            ) : (
                                <span>Available only for admitted students</span>
                            )}
                        </div>
                    </div>
                    <button
                        className='studentIdButton'
                        disabled={isLoading || !canViewStudentId}
                        onClick={(event) => {
                            event.stopPropagation();
                            if (!canViewStudentId) return;
                            navigate("/student-id");
                        }}
                    >
                        {isLoading ? "Loading" : canViewStudentId ? "View" : "Locked"}
                    </button>
                </div>

            </div>
        </div>

        {showInterviewDashboardCard ? (
            <div className='admissionFeeDashboardCard dashboardInterviewCard'>
                <div className='dashboardInterviewContent'>
                    <span>Interview invitation</span>
                    <h5>{interviewRecord?.status === "active" ? 'Your interview room is open' : 'Your admissions interview is ready'}</h5>
                    <p>
                        {interviewRecord?.status === "scheduled"
                            ? `Join on ${formatInterviewDateTime(interviewRecord?.scheduledFor)} using your student account.`
                            : 'Open your interview page to join the admissions team.'}
                    </p>
                    <div className='dashboardInterviewMeta'>
                        <strong>{getInterviewStatusLabel(interviewRecord?.status)}</strong>
                        <span>{formatInterviewDateTime(interviewRecord?.scheduledFor || interviewRecord?.startedAt)}</span>
                    </div>
                </div>
                <button type='button' onClick={() => navigate(getInterviewPortalPath(interviewMeetingId))}>
                    Open Interview
                </button>
            </div>
        ) : null}

        <div className='dashFlex1'>
            <div className='dash3'>
                <hr />
                <h6>Become a Code Chef</h6>
                <p>Your journey to becoming a code chef starts here</p>
                <img src={dashTwo} alt="" />
            </div>

            <div className='dash4'>

            </div>

            <div className='dash5'>

                <div className='dashDateCont'>
                    <h2>{localDayNumber}</h2>
                </div>
                <span className='dash5Label'>Your Local Time</span>
                <h6>{formattedTime}</h6>
                <p>{localDateLabel}</p>
                <small>{localPlaceLabel || 'Your location'} • {localTimezoneLabel}</small>

            </div>

        </div>



    </div>
  )
}

export default DashComp
